import { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

import type { EnrichedLine } from "../../services/distributionPreviewService";
import { DebouncedSearchField } from "../DebouncedSearchField";
import {
  filterActiveLinesByQuery,
  groupActiveLinesBySupplier,
} from "./deferred-filter.helper";
import { ActiveLinesTable } from "./ActiveLinesTable";

/**
 * Auditoría 2026-05-05 — panel principal de líneas activas del dialog de
 * Revisión previa, agrupado por proveedor con filtros de proveedor +
 * producto. Mismo patrón UX que `DeferredBySupplierPanel`.
 *
 * Diferencias con el panel de diferidas:
 *   - Toggle del checkbox NO es "re-promover" sino "incluir/excluir"
 *     (excludedSet del flujo existente).
 *   - Columnas extra: Source (chip stock_alert/reposicion/seeding),
 *     Denegación reclamada.
 *   - Stock como Chip color (success/error) en lugar de número plano.
 */
export interface ActiveLinesBySupplierPanelProps {
  lines: EnrichedLine[];
  excludedSet: Set<string>;
  onToggle: (recommendation_id: string) => void;
}

export function ActiveLinesBySupplierPanel({
  lines,
  excludedSet,
  onToggle,
}: ActiveLinesBySupplierPanelProps) {
  const [supplierQuery, setSupplierQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");

  const allBuckets = useMemo(
    () => groupActiveLinesBySupplier(lines, excludedSet),
    [lines, excludedSet],
  );

  // Si hay pocos buckets (≤3) o pocas líneas en total (≤30), expandir
  // todos por default — el operador ve la tabla sin tener que clickear.
  // Con muchos buckets (134 proveedores con 6K líneas), todos colapsados
  // y el operador filtra/expande lo que necesita.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    if (allBuckets.length <= 3 || lines.length <= 30) {
      return new Set(allBuckets.map((b) => b.supplier_name ?? "_null"));
    }
    return new Set();
  });
  const filteredBuckets = useMemo(
    () => filterActiveLinesByQuery(allBuckets, supplierQuery, productQuery),
    [allBuckets, supplierQuery, productQuery],
  );

  const totals = useMemo(() => {
    const linesCount = filteredBuckets.reduce(
      (acc, b) => acc + b.total_lines,
      0,
    );
    const units = filteredBuckets.reduce((acc, b) => acc + b.total_units, 0);
    return { lines: linesCount, units };
  }, [filteredBuckets]);

  const isFiltered =
    supplierQuery.trim().length > 0 || productQuery.trim().length > 0;
  const totalBuckets = allBuckets.length;
  const totalLines = lines.length;
  const totalUnits = lines.reduce(
    (acc, l) =>
      excludedSet.has(l.recommendation_id) ? acc : acc + l.qty_planned,
    0,
  );

  if (lines.length === 0) {
    return null;
  }

  const toggleExpand = (key: string) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpanded(next);
  };

  const toggleBucket = (bucketLines: EnrichedLine[]) => {
    const allIncluded = bucketLines.every(
      (l) => !excludedSet.has(l.recommendation_id),
    );
    bucketLines.forEach((l) => {
      const isExcluded = excludedSet.has(l.recommendation_id);
      if (allIncluded && !isExcluded) onToggle(l.recommendation_id);
      else if (!allIncluded && isExcluded) onToggle(l.recommendation_id);
    });
  };

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", mt: 1 }}>
      {/* Header del panel */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: "grey.100",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            Líneas a transferir
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totals.lines.toLocaleString("es-MX")} de{" "}
            {totalLines.toLocaleString("es-MX")} líneas ·{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {totals.units.toLocaleString("es-MX")} u
            </Box>{" "}
            (total wave: {totalUnits.toLocaleString("es-MX")} u) ·{" "}
            {filteredBuckets.length} proveedor
            {filteredBuckets.length === 1 ? "" : "es"}
            {isFiltered && (
              <Box
                component="span"
                sx={{ ml: 1, color: "warning.dark", fontWeight: 600 }}
              >
                (filtrado de {totalBuckets})
              </Box>
            )}
          </Typography>
        </Box>
      </Stack>

      {/* Filtros */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          px: 2.5,
          py: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <DebouncedSearchField
          placeholder="Proveedor: yuliana, tramontina (OR)"
          onDebouncedChange={setSupplierQuery}
          sx={{ flex: 1 }}
        />
        <DebouncedSearchField
          placeholder="Producto: vaso, -termo (excluye)"
          onDebouncedChange={setProductQuery}
          sx={{ flex: 1 }}
        />
      </Stack>

      {/* Lista de buckets de proveedor */}
      <Box sx={{ maxHeight: "65vh", overflowY: "auto" }}>
        {filteredBuckets.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">
              Ningún proveedor coincide con los filtros aplicados.
            </Typography>
          </Box>
        )}
        {filteredBuckets.map((bucket, idx) => {
          const key = bucket.supplier_name ?? "_null";
          const allIncluded = bucket.lines.every(
            (l) => !excludedSet.has(l.recommendation_id),
          );
          const someIncluded =
            !allIncluded &&
            bucket.lines.some((l) => !excludedSet.has(l.recommendation_id));
          const isExpanded = expanded.has(key);
          const supplierLabel = bucket.supplier_name ?? "Sin proveedor asignado";
          return (
            <Box
              key={key}
              sx={{
                borderBottom: idx === filteredBuckets.length - 1 ? 0 : 1,
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 2.5,
                  py: 1,
                  bgcolor: isExpanded ? "action.hover" : "transparent",
                  "&:hover": { bgcolor: "action.hover" },
                  transition: "background-color 150ms",
                }}
              >
                <Checkbox
                  size="small"
                  checked={allIncluded}
                  indeterminate={someIncluded}
                  onChange={() => toggleBucket(bucket.lines)}
                  inputProps={{
                    "aria-label": `Toggle todo: ${supplierLabel}`,
                  }}
                  sx={{ p: 0.5 }}
                />
                <IconButton
                  size="small"
                  onClick={() => toggleExpand(key)}
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? "Colapsar" : "Expandir"}
                  sx={{ p: 0.5 }}
                >
                  {isExpanded ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </IconButton>
                <Tooltip title={supplierLabel} placement="top">
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: bucket.supplier_name
                        ? "text.primary"
                        : "text.secondary",
                      fontStyle: bucket.supplier_name ? "normal" : "italic",
                      cursor: "pointer",
                    }}
                    onClick={() => toggleExpand(key)}
                  >
                    {supplierLabel}
                  </Typography>
                </Tooltip>
                <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                  <Chip
                    label={`${bucket.total_lines.toLocaleString("es-MX")} / ${bucket.lines.length} líneas`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: "0.7rem",
                      height: 22,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  />
                  <Chip
                    label={`${bucket.total_units.toLocaleString("es-MX")} u`}
                    size="small"
                    color="success"
                    variant="filled"
                    sx={{
                      fontSize: "0.7rem",
                      height: 22,
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  />
                </Stack>
              </Stack>
              {isExpanded && (
                <ActiveLinesTable
                  lines={bucket.lines}
                  excludedSet={excludedSet}
                  onToggle={onToggle}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}
